import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { reset } from "./reset";
import { IUserPermission } from "@grudder/types/user-permission.types";
import { AUTH_POST, AUTH_PUT } from "@grudder/app/actions-client";
import { config } from "@grudder/lib/config";

interface PermissionState {
  allPermissions: IUserPermission[];
  userPermissions: IUserPermission[];
}

// Define the initial state
const initialState: PermissionState = {
  allPermissions: [],
  userPermissions: [],
};

// Define the slice
const permissionSlice = createSlice({
  name: "permission",
  initialState,
  reducers: {
    setUserPermissions: (state, action: PayloadAction<any[]>) => {
      state.userPermissions = action.payload;
    },
    setAllPermissions: (state, action: PayloadAction<any[]>) => {
      state.allPermissions = action.payload;
    },
    updateAllPermissions: (state, action: PayloadAction<any[]>) => {
      const allPermissionMap = new Map(state.allPermissions.map((allPermission) => [allPermission.id, allPermission]));
      //loop current permissions and update the one that has changed
      action.payload.forEach((permission) => {
        allPermissionMap.set(permission._id, permission);
      });
      state.allPermissions = Array.from(allPermissionMap.values());
    },
  },
  extraReducers: (builder) => {
    builder.addCase(reset, () => initialState)
      .addCase(updateSinglePermissionAction.fulfilled, (state, action) => {
        const updatedPermission = action.payload;
        const permissionIndex = state.allPermissions.findIndex((p) => p._id === updatedPermission._id);
        if (permissionIndex !== -1) {
          state.allPermissions[permissionIndex] = updatedPermission;
        }
      })
      .addCase(updateBulkPermissionAction.fulfilled, (state, action) => {
        const updatedPermissions = action.payload;
        updatedPermissions.forEach((updatedPermission: IUserPermission) => {
          const permissionIndex = state.allPermissions.findIndex((p) => p._id === updatedPermission._id);
          if (permissionIndex !== -1) {
            state.allPermissions[permissionIndex] = updatedPermission;
          }
        });
      })
      .addCase(createPermissionAction.fulfilled, (state, action) => {
        state.allPermissions.push(action.payload);
      });

  },
});


export const { setAllPermissions, setUserPermissions, updateAllPermissions } = permissionSlice.actions;

export default permissionSlice.reducer;




export const updateSinglePermissionAction = createAsyncThunk("permission/singleUpdate", async ({ existingPermission, updateData }: { existingPermission: any, updateData: any }, { rejectWithValue }) => {
  try {
    return await AUTH_PUT(`${config.baseURL}/api/user-permissions/${existingPermission._id}`, updateData, {
      tags: ['user-permissions', `permission:${existingPermission._id}`]
    });

  } catch (error) {
    rejectWithValue(error)
    console.log({ error });
  }
});

export const updateBulkPermissionAction = createAsyncThunk("permission/bulkUpdate", async ({ input }: { input: any }, { rejectWithValue }) => {
  try {
    return AUTH_POST(`${config.baseURL}/api/user-permissions/bulk-update`, input, {
      tags: ['user-permissions', 'bulk-update']
    });

  } catch (error) {
    rejectWithValue(error)
    console.log({ error });
  }
});

export const createPermissionAction = createAsyncThunk("permission/create", async ({ permissionData }: { permissionData: any }, { rejectWithValue }) => {
  try {
    return AUTH_POST(`${config.baseURL}/api/user-permissions`, permissionData, {
      tags: ['user-permissions']
    });
  } catch (error) {
    rejectWithValue(error)
    console.log({ error });
  }
});
